/** FEC candidate detail API helpers (backend proxy). Default cycle: 2026. */

export const FEC_DEFAULT_CYCLE = 2026;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const OFFICE_CONFIG = {
  H: { label: "House of Representatives", shortLabel: "House" },
  S: { label: "U.S. Senate", shortLabel: "Senate" },
  P: { label: "President", shortLabel: "President" },
};

export function toTitleCase(value) {
  return value
    ? value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\s+/g, " ")
    : value;
}

export function compactMoney(value) {
  const num = value == null ? null : Number(value);
  if (num == null || Number.isNaN(num)) return "—";
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 1000) return `$${Math.round(num / 1000)}K`;
  return `$${Math.round(num).toLocaleString("en-US")}`;
}

export function money(value) {
  const num = value == null ? null : Number(value);
  if (num == null || Number.isNaN(num)) return "—";
  try {
    return num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  } catch {
    return `$${Math.round(num).toLocaleString("en-US")}`;
  }
}

export function isValidUrl(urlString) {
  if (!urlString || typeof urlString !== "string") return false;
  const trimmed = urlString.trim();
  if (!trimmed || trimmed.includes(" ")) return false;
  const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
  return urlPattern.test(trimmed) || trimmed.includes(".");
}

function getDistrictLabel(state, district) {
  if (!state) return "";
  if (!district) return state;
  if (district === "AL" || district === "00" || district === "0") return `${state}-AL`;
  return `${state}-${String(district).padStart(2, "0")}`;
}

export function normalizeCandidate(data) {
  if (!data) return null;
  const officeCode = data.office_code || data.office || "H";
  const districtRaw = data.district ?? data.district_number ?? "";
  const district = districtRaw === "00" || districtRaw === "0" ? "AL" : districtRaw;
  const state = data.state || "";
  const party = data.party || data.party_code || "IND";
  const incumbent = Boolean(data.incumbent || data.incumbent_challenge === "I");
  const openSeat = Boolean(data.open_seat || data.incumbent_challenge === "O");
  return {
    id: data.id || data.candidate_id,
    name: toTitleCase(data.name || "Unnamed Candidate"),
    party,
    officeCode,
    officeLabel: OFFICE_CONFIG[officeCode]?.shortLabel || "House",
    state,
    district,
    districtLabel: data.district_label || getDistrictLabel(state, district),
    incumbent,
    openSeat,
    status: data.candidate_status || data.status || "",
    website: data.website || data.candidate_url || data.candidate_website || null,
    hasCivixThreadAccount: Boolean(data.has_civixthread_account),
    civixThreadUserId: data.civixthread_user_id || null,
  };
}

export function normalizeTotals(data) {
  if (!data) return null;
  return {
    cycle: data.cycle,
    receipts: Number(data.total_receipts ?? data.receipts ?? 0),
    disbursements: Number(data.total_disbursements ?? data.disbursements ?? 0),
    cash_on_hand_end_period: Number(data.cash_on_hand_end_period ?? 0),
    debts_owed_by_committee: Number(data.debts_owed_by_committee ?? 0),
    individual_percentage: Number(data.individual_percentage ?? 0),
    pac_percentage: Number(data.pac_percentage ?? 0),
  };
}

export function normalizeCommittee(item) {
  return {
    id: item.committee_id || item.id,
    name: item.name || "Unknown Committee",
    designation: item.designation || "",
    state: item.state || "",
    treasurer: item.treasurer_name || item.treasurer || "",
    website: item.website || item.website_url || null,
  };
}

async function fetchJson(url, signal) {
  const response = await fetch(url, { cache: "no-store", signal });
  if (!response.ok) {
    let details = "";
    try {
      details = await response.text();
    } catch {
      details = "";
    }
    throw new Error(details || `Request failed (${response.status})`);
  }
  return response.json();
}

export async function fetchCandidateDetail(candidateId, cycle = FEC_DEFAULT_CYCLE, signal) {
  const data = await fetchJson(`${API_BASE_URL}/api/fec/candidates/${candidateId}/?cycle=${cycle}`, signal);
  return normalizeCandidate(data);
}

export async function fetchCandidateTotals(candidateId, cycle = FEC_DEFAULT_CYCLE, signal) {
  const data = await fetchJson(`${API_BASE_URL}/api/fec/candidates/${candidateId}/totals/?cycle=${cycle}`, signal);
  return normalizeTotals(data);
}

export async function fetchCandidateCommittees(candidateId, cycle = FEC_DEFAULT_CYCLE, signal) {
  const data = await fetchJson(`${API_BASE_URL}/api/fec/candidates/${candidateId}/committees/?cycle=${cycle}`, signal);
  const committees = data?.committees || data || [];
  return Array.isArray(committees) ? committees.map(normalizeCommittee) : [];
}

export async function fetchCandidateContributors(candidateId, cycle = FEC_DEFAULT_CYCLE, signal) {
  const data = await fetchJson(`${API_BASE_URL}/api/candidate/${candidateId}/top_contributors/?cycle=${cycle}`, signal);
  return Array.isArray(data) ? data : [];
}

export async function fetchSuperPACSponsors(candidateId, cycle = FEC_DEFAULT_CYCLE, signal) {
  const data = await fetchJson(`${API_BASE_URL}/api/candidate/${candidateId}/super_pac_sponsors/?cycle=${cycle}`, signal);
  return data || null;
}

export async function loadCandidatePageData(candidateId, cycle = FEC_DEFAULT_CYCLE, signal) {
  const [candidateData, totalsData, contributorsData, pacData, committeesData] = await Promise.all([
    fetchCandidateDetail(candidateId, cycle, signal).catch(() => null),
    fetchCandidateTotals(candidateId, cycle, signal).catch(() => null),
    fetchCandidateContributors(candidateId, cycle, signal).catch(() => []),
    fetchSuperPACSponsors(candidateId, cycle, signal).catch(() => null),
    fetchCandidateCommittees(candidateId, cycle, signal).catch(() => []),
  ]);
  return {
    candidate: candidateData,
    totals: totalsData,
    contributors: Array.isArray(contributorsData) ? contributorsData : [],
    superPACData: pacData,
    committees: Array.isArray(committeesData) ? committeesData : [],
  };
}
